// @ts-nocheck
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { GetShipmentsApiParameter } from "@store/Framework/Services/ShipmentService";
import loadShipments from "@store/Framework/Store/Greif/Data/Shipments/Handlers/LoadShipments";
import {
    getShipmentsDataView,
    ShipmentsDataViewContext,
} from "@store/Framework/Store/Greif/Data/Shipments/ShipmentsSelectors";
import loadShipmentStatuses from "@store/Framework/Store/Greif/Data/ShipmentStatuses/Handlers/LoadShipmentStatuses";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatuses/ShipmentStatusesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateSearchFields";
import setParameter from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/SetParameter";
import qs from "qs";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import useGreifLocations from "@store/Framework/Store/Greif/Components/LocationFilters/useGreifLocations";

const mapStateToProps = (state: ApplicationState) => {
    const { parameter, getShipmentStatusesParameter } = state.greif.pages.shipmentHistory;

    return {
        parameter,
        getShipmentStatusesParameter,
        settings: getSettingsCollection(state),
        shipmentsDataView: getShipmentsDataView(state, parameter),
        shouldLoadShipmentStatuses: !getShipmentStatusesDataView(state).value,
        location: getLocation(state),
        currentShipToValue: getCurrentShipToState(state).value,
    };
};

const mapDispatchToProps = {
    loadShipments,
    loadShipmentStatuses,
    updateSearchFields,
    setParameter,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const ShipmentHistoryPage = ({
    id,
    history,
    location,
    settings,
    shipmentsDataView,
    shouldLoadShipmentStatuses,
    parameter,
    getShipmentStatusesParameter,
    currentShipToValue,
    loadShipments,
    loadShipmentStatuses,
    updateSearchFields,
    setParameter,
}: Props) => {
    useGreifLocations();

    let firstLoad = false;

    const getShipmentsApiParameter = parseQueryString<GetShipmentsApiParameter>(location.search);

    useEffect(() => {
        firstLoad = true;
        const pageSizeCookie = getCookie("ShipmentHistory-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            if (pageSize) {
                getShipmentsApiParameter.pageSize = pageSize;
            }
            setParameter({
                customerSequence: currentShipToValue?.customerSequence,
                ...getShipmentsApiParameter,
            });
        } else if (settings.orderSettings.lookBackDays > 0) {
            const fromDate = new Date(Date.now() - settings.orderSettings.lookBackDays * 60 * 60 * 24 * 1000);
            setParameter({
                customerSequence: currentShipToValue?.customerSequence,
                ...getShipmentsApiParameter,
                fromDate: convertDateToDateOnlyString(fromDate),
                pageSize,
            });
        } else {
            setParameter({
                customerSequence: currentShipToValue?.customerSequence,
                ...getShipmentsApiParameter,
                pageSize,
            });
        }

        if (shouldLoadShipmentStatuses) {
            loadShipmentStatuses(getShipmentStatusesParameter);
        }
    }, [currentShipToValue?.customerSequence]);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(parameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [parameter]);

    useEffect(() => {
        if (!firstLoad && !shipmentsDataView.value && !shipmentsDataView.isLoading) {
            loadShipments(parameter);
        } else if (!shipmentsDataView.isLoading && shipmentsDataView.value?.length === 0 && parameter.page) {
            updateSearchFields({ page: parameter.page - 1 });
        }
    });

    return (
        <Page
            data-test-selector="shipmentHistory"
            css={css`
                position: relative;
                flex-grow: 1;
            `}
        >
            <ShipmentsDataViewContext.Provider value={shipmentsDataView}>
                <Zone contentId={id} zoneName="Content" />
            </ShipmentsDataViewContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(ShipmentHistoryPage)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
